import { BsPencil, BsPlusCircle } from 'react-icons/bs';
import { Dispatch, FC, SetStateAction, useMemo, useRef, useState } from 'react';
import { generalHelpers } from '@monorepo/helpers';
import { helpers } from 'src/services';
import { MeetingAttendanceCrudModal } from './MeetingAttendanceCrudModal';
import { MeetingAttendanceTableFilter } from './MeetingAttendanceTableFilter';
import { MeetingAttendanceTablePrint } from './MeetingAttendanceTablePrint';
import { typeMeeting } from '@monorepo/models';
import { useGetIsDarkMode } from 'src/custom-hooks';

// export type typeAttendanceMessage = 'success' | 'error' | 'warning';

type propsType = {
    meetings: typeMeeting[];
    setRefreshCounter: Dispatch<SetStateAction<number>>;
    // showToastMessage: (message: string, type: typeAttendanceMessage) => void;
};

export const MeetingAttendanceTable: FC<propsType> = ({ meetings, setRefreshCounter }) => {
    const [meetingToEdit, setMeetingToEdit] = useState<typeMeeting | null>(null);
    const [monthFilter, setMonthFilter] = useState('');
    const [showCrudModal, setShowCrudModal] = useState(false);
    const isDarkMode = useGetIsDarkMode();
    const tableRef = useRef<HTMLTableElement | null>(null);

    const sample: typeMeeting[] = useMemo(() => {
        const _sample = meetings
            .filter(m => !monthFilter || new Date(m.date).toISOString().slice(0, 7) === monthFilter)
            .map(m => ({
                ...m,
                attendance: {
                    ...m.attendance,
                    total: m.attendance.inPerson + m.attendance.zoom + m.attendance.youtube
                }
            }))
        ;
        return _sample;
    }, [meetings, monthFilter]);

    const totals: typeMeeting = useMemo(() => {
        const inPerson = sample.reduce((ac, m) => ac + m.attendance.inPerson, 0);
        const zoom = sample.reduce((ac, m) => ac + m.attendance.zoom, 0);
        const youtube = sample.reduce((ac, m) => ac + m.attendance.youtube, 0);
        const total = sample.reduce((ac, m) => ac + m.attendance.inPerson + m.attendance.zoom + m.attendance.youtube, 0);
        const totals = {
            attendance: {
                inPerson,
                notes: '',
                total,
                youtube,
                zoom
            },
            date: 'TOTALES DEL MES',
            id: 1
        } as typeMeeting;
        return totals;
    }, [sample]);

    const averages: typeMeeting = useMemo(() => {
        const length = sample.filter(m => m.attendance.total! > 0).length;
        const _averages = {
            attendance: {
                inPerson: Math.round(totals.attendance.inPerson / length),
                notes: '*No se consideran las "reuniones" sin asistencia (por asamblea, etc.)',
                total: Math.round(totals.attendance.total! / length),
                youtube: Math.round(totals.attendance.youtube / length),
                zoom: Math.round(totals.attendance.zoom / length)
            },
            date: 'PROMEDIOS DEL MES',
            id: 2
        } as typeMeeting;
        return _averages;
    }, [sample, totals]);

    const deviation: typeMeeting = useMemo(() => {
        const length = sample.filter(a => a.attendance.total! > 0).length;
        const deviation = {
            attendance: {
                inPerson: Math.round(
                    Math.sqrt(
                        sample
                            .filter(m => !!m.attendance.total)
                            .map(m => (m.attendance.inPerson - averages.attendance.inPerson) ** 2)
                            .reduce((ac, a) => ac + a, 0) / length
                    )
                ),
                notes: '',
                total: Math.round(
                    Math.sqrt(
                        sample
                            .filter(m => !!m.attendance.total)
                            .map(m => (m.attendance.total! - averages.attendance.total!) ** 2)
                            .reduce((ac, a) => ac + a, 0) / length
                    )
                ),
                youtube: Math.round(
                    Math.sqrt(
                        sample
                            .filter(m => !!m.attendance.total)
                            .map(m => (m.attendance.youtube - averages.attendance.youtube) ** 2)
                            .reduce((ac, a) => ac + a, 0) / length
                    )
                ),
                zoom: Math.round(
                    Math.sqrt(
                        sample
                            .filter(m => !!m.attendance.total)
                            .map(m => (m.attendance.zoom - averages.attendance.zoom) ** 2)
                            .reduce((ac, a) => ac + a, 0) / length
                    )
                )
            },
            date: 'DESVÍOS R/PROMEDIOS',
            id: 4
        } as typeMeeting;
        return deviation;
    }, [averages, sample]);

    return (
        <>
        {(showCrudModal || meetingToEdit) &&
            <MeetingAttendanceCrudModal
                closeModal={() => {
                    setShowCrudModal(false);
                    setMeetingToEdit(null);
                }}
                meetingToEdit={meetingToEdit}
                meetings={meetings}
                setRefreshCounter={setRefreshCounter}
            />
        }
        <div className={'d-flex justify-content-center mt-2 mb-4'}>
            <button className={'btn btn-general-red'}
                onClick={() => setShowCrudModal(true)}
            >
                <BsPlusCircle className={'mb-1 me-2'} />Agregar Reunión
            </button>
        </div>
        <MeetingAttendanceTableFilter
            meetings={meetings}
            monthFilter={monthFilter}
            setMonthFilter={setMonthFilter}
        />
        {monthFilter &&
            <MeetingAttendanceTablePrint
                meetings={[...sample, totals, averages]}
                month={monthFilter}
                tableRef={tableRef}
            />
        }
        <style>
            {`
                .hide-column-pdf td:nth-child(10), .hide-column-pdf th:nth-child(9) {
                    display: none;
                }
            `}
        </style>
        <div className={'table-responsive mt-3'}>
            <table className={`table table-striped table-bordered ${isDarkMode ? 'table-dark' : ''} w-100 font-monospace mx-auto`} ref={tableRef}>
                <thead>
                    <tr className={'text-center'}>
                        <th colSpan={2} className={'text-start'}>Fecha</th>
                        <th className={'text-start'}>Sem</th>
                        <th className={'text-start'}>Año</th>
                        <th>Presencial</th>
                        <th>Zoom</th>
                        <th>YouTube</th>
                        <th>Total</th>
                        <th className={'text-start'} style={{ minWidth: '150px' }}>Notas</th>
                        <th style={{ width: '100px' }}>Editar</th>
                    </tr>
                </thead>
                <tbody className={'text-center'}>
                    {sample.map(m =>
                        <tr key={m.id}>
                            <td className={'text-nowrap text-start'}>
                                {helpers.GetDayOfWeekFromDateString(m.date)}
                            </td>
                            <td className={'text-nowrap text-start'}>
                                {generalHelpers.InvertDateString(m.date)}
                            </td>
                            <td className={'text-nowrap text-start'}>
                                {m.weekNumber}
                            </td>
                            <td className={'text-nowrap text-start'}>
                                {m.year}
                            </td>
                            <td>
                                {m.attendance.inPerson}
                            </td>
                            <td>
                                {m.attendance.zoom}
                            </td>
                            <td>
                                {m.attendance.youtube}
                            </td>
                            <td>
                                {m.attendance.inPerson + m.attendance.zoom + m.attendance.youtube}
                            </td>
                            <td className={'text-start'}>
                                {m.attendance.notes}
                            </td>
                            <td>
                                <button className={'btn btn-sm btn-yellow w-100'}
                                    onClick={() => setMeetingToEdit(m)}
                                >
                                    <BsPencil />
                                </button>
                            </td>
                        </tr>
                    )}
                    {monthFilter &&
                        <>
                        <tr>
                            <td colSpan={10}></td>
                        </tr>
                        <tr>
                            <td className={'text-start'} colSpan={4}>
                                {totals.date}
                            </td>
                            <td>
                                {totals.attendance.inPerson}
                            </td>
                            <td>
                                {totals.attendance.zoom}
                            </td>
                            <td>
                                {totals.attendance.youtube}
                            </td>
                            <td>
                                {totals.attendance.total}
                            </td>
                            <td colSpan={2}></td>
                        </tr>
                        <tr>
                            <td className={'text-start'} colSpan={4}>
                                {averages.date}
                            </td>
                            <td>
                                {averages.attendance.inPerson}
                            </td>
                            <td>
                                {averages.attendance.zoom}
                            </td>
                            <td>
                                {averages.attendance.youtube}
                            </td>
                            <td>
                                {averages.attendance.total}
                            </td>
                            <td colSpan={2} className={'text-start small'}>
                                {averages.attendance.notes}
                            </td>
                        </tr>
                        <tr>
                            <td className={'text-start'} colSpan={4}>
                                {deviation.date}
                            </td>
                            <td>
                                {deviation.attendance.inPerson}
                            </td>
                            <td>
                                {deviation.attendance.zoom}
                            </td>
                            <td>
                                {deviation.attendance.youtube}
                            </td>
                            <td>
                                {deviation.attendance.total}
                            </td>
                            <td colSpan={2} className={'text-start small'}>
                                {deviation.attendance.notes}
                            </td>
                        </tr>
                        </>
                    }
                </tbody>
            </table>
        </div>
        </>
    );
}
